import type { Locale } from '@/i18n/config';

// Localized Bible book names for the reading plan.
// Each map covers full names plus the abbreviations actually used in
// `src/data/bible-reading-plan.ts`. English is the source-of-truth key set.

export const bibleBooksFr: Record<string, string> = {
  // Old Testament - Ancien Testament

  // Pentateuch / Pentateuque
  'Genesis': 'Genèse',
  'Gen': 'Gn',
  'Gen.': 'Gn',
  'Exodus': 'Exode',
  'Exod': 'Ex',
  'Exod.': 'Ex',
  'Leviticus': 'Lévitique',
  'Lev': 'Lv',
  'Lev.': 'Lv',
  'Numbers': 'Nombres',
  'Num': 'Nb',
  'Num.': 'Nb',
  'Deuteronomy': 'Deutéronome',
  'Deut': 'Dt',
  'Deut.': 'Dt',

  // Historical Books / Livres historiques
  'Joshua': 'Josué',
  'Josh': 'Jos',
  'Josh.': 'Jos',
  'Judges': 'Juges',
  'Judg': 'Jg',
  'Judg.': 'Jg',
  'Ruth': 'Ruth',
  '1 Samuel': '1 Samuel',
  '1 Sam': '1 S',
  '1 Sam.': '1 S',
  '2 Samuel': '2 Samuel',
  '2 Sam': '2 S',
  '2 Sam.': '2 S',
  '1 Kings': '1 Rois',
  '1 Kgs': '1 R',
  '1 Kgs.': '1 R',
  '2 Kings': '2 Rois',
  '2 Kgs': '2 R',
  '2 Kgs.': '2 R',
  '1 Chronicles': '1 Chroniques',
  '1 Chron': '1 Ch',
  '1 Chron.': '1 Ch',
  '1 Chro': '1 Ch',
  '1 Chro.': '1 Ch',
  '2 Chronicles': '2 Chroniques',
  '2 Chron': '2 Ch',
  '2 Chron.': '2 Ch',
  '2 Chro': '2 Ch',
  '2 Chro.': '2 Ch',
  'Ezra': 'Esdras',
  'Nehemiah': 'Néhémie',
  'Neh': 'Né',
  'Neh.': 'Né',
  'Esther': 'Esther',
  'Est': 'Est',
  'Est.': 'Est',

  // Wisdom Books / Livres poétiques et sapientiaux
  'Job': 'Job',
  'Psalms': 'Psaumes',
  'Psalm': 'Psaume',
  'Ps': 'Ps',
  'Ps.': 'Ps',
  'Proverbs': 'Proverbes',
  'Prov': 'Pr',
  'Prov.': 'Pr',
  'Pro': 'Pr',
  'Pro.': 'Pr',
  'Ecclesiastes': 'Ecclésiaste',
  'Eccl': 'Ec',
  'Eccl.': 'Ec',
  'Song of Solomon': 'Cantique des Cantiques',
  'Song': 'Ct',
  'S.O.S.': 'Ct',
  'SOS': 'Ct',

  // Major Prophets / Grands prophètes
  'Isaiah': 'Ésaïe',
  'Isa': 'És',
  'Isa.': 'És',
  'Jeremiah': 'Jérémie',
  'Jer': 'Jr',
  'Jer.': 'Jr',
  'Lamentations': 'Lamentations',
  'Lamentation': 'Lamentations',
  'Lam': 'Lm',
  'Lam.': 'Lm',
  'Ezekiel': 'Ézéchiel',
  'Ezek': 'Éz',
  'Ezek.': 'Éz',
  'Daniel': 'Daniel',
  'Dan': 'Dn',
  'Dan.': 'Dn',

  // Minor Prophets / Petits prophètes
  'Hosea': 'Osée',
  'Hos': 'Os',
  'Hos.': 'Os',
  'Joel': 'Joël',
  'Amos': 'Amos',
  'Obadiah': 'Abdias',
  'Oba': 'Ab',
  'Oba.': 'Ab',
  'Obad': 'Ab',
  'Obad.': 'Ab',
  'Jonah': 'Jonas',
  'Jon': 'Jon',
  'Jon.': 'Jon',
  'Micah': 'Michée',
  'Mic': 'Mi',
  'Mic.': 'Mi',
  'Nahum': 'Nahum',
  'Nah': 'Na',
  'Nah.': 'Na',
  'Habakkuk': 'Habacuc',
  'Hab': 'Ha',
  'Hab.': 'Ha',
  'Zephaniah': 'Sophonie',
  'Zeph': 'So',
  'Zeph.': 'So',
  'Haggai': 'Aggée',
  'Hag': 'Ag',
  'Hag.': 'Ag',
  'Zechariah': 'Zacharie',
  'Zech': 'Za',
  'Zech.': 'Za',
  'Malachi': 'Malachie',
  'Mal': 'Ml',
  'Mal.': 'Ml',

  // New Testament - Nouveau Testament

  // Gospels / Évangiles
  'Matthew': 'Matthieu',
  'Matt': 'Mt',
  'Matt.': 'Mt',
  'Mark': 'Marc',
  'Mk': 'Mc',
  'Mk.': 'Mc',
  'Luke': 'Luc',
  'Lk': 'Lc',
  'Lk.': 'Lc',
  'John': 'Jean',
  'Jn': 'Jn',
  'Jn.': 'Jn',

  // History / Histoire
  'Acts': 'Actes',
  'Act': 'Ac',
  'Act.': 'Ac',

  // Pauline Epistles / Épîtres pauliniennes
  'Romans': 'Romains',
  'Rom': 'Rm',
  'Rom.': 'Rm',
  '1 Corinthians': '1 Corinthiens',
  '1 Cor': '1 Co',
  '1 Cor.': '1 Co',
  '2 Corinthians': '2 Corinthiens',
  '2 Cor': '2 Co',
  '2 Cor.': '2 Co',
  'Galatians': 'Galates',
  'Gal': 'Ga',
  'Gal.': 'Ga',
  'Ephesians': 'Éphésiens',
  'Eph': 'Ép',
  'Eph.': 'Ép',
  'Philippians': 'Philippiens',
  'Phil': 'Ph',
  'Phil.': 'Ph',
  'Colossians': 'Colossiens',
  'Col': 'Col',
  'Col.': 'Col',
  '1 Thessalonians': '1 Thessaloniciens',
  '1 Thess': '1 Th',
  '1 Thess.': '1 Th',
  '2 Thessalonians': '2 Thessaloniciens',
  '2 Thess': '2 Th',
  '2 Thess.': '2 Th',
  '1 Timothy': '1 Timothée',
  '1 Tim': '1 Tm',
  '1 Tim.': '1 Tm',
  '2 Timothy': '2 Timothée',
  '2 Tim': '2 Tm',
  '2 Tim.': '2 Tm',
  'Titus': 'Tite',
  'Tit': 'Tt',
  'Tit.': 'Tt',
  'Philemon': 'Philémon',
  'Phlm': 'Phm',
  'Phlm.': 'Phm',

  // General Epistles / Épîtres générales
  'Hebrews': 'Hébreux',
  'Hebrew': 'Hébreux',
  'Heb': 'Hé',
  'Heb.': 'Hé',
  'James': 'Jacques',
  'Jas': 'Jc',
  'Jas.': 'Jc',
  '1 Peter': '1 Pierre',
  '1 Pet': '1 P',
  '1 Pet.': '1 P',
  '2 Peter': '2 Pierre',
  '2 Pet': '2 P',
  '2 Pet.': '2 P',
  '1 John': '1 Jean',
  '1 Jn': '1 Jn',
  '1 Jn.': '1 Jn',
  '2 John': '2 Jean',
  '2 Jn': '2 Jn',
  '2 Jn.': '2 Jn',
  '3 John': '3 Jean',
  '3 Jn': '3 Jn',
  '3 Jn.': '3 Jn',
  'Jude': 'Jude',

  // Apocalyptic / Apocalyptique
  'Revelation': 'Apocalypse',
  'Rev': 'Ap',
  'Rev.': 'Ap',
};

// Spanish (Reina-Valera tradition) — used by most Latin American Protestants.
export const bibleBooksEs: Record<string, string> = {
  // Pentateuco
  'Genesis': 'Génesis',
  'Gen': 'Gn',
  'Gen.': 'Gn',
  'Exodus': 'Éxodo',
  'Exod': 'Éx',
  'Exod.': 'Éx',
  'Leviticus': 'Levítico',
  'Lev': 'Lv',
  'Lev.': 'Lv',
  'Numbers': 'Números',
  'Num': 'Nm',
  'Num.': 'Nm',
  'Deuteronomy': 'Deuteronomio',
  'Deut': 'Dt',
  'Deut.': 'Dt',

  // Libros históricos
  'Joshua': 'Josué',
  'Josh': 'Jos',
  'Josh.': 'Jos',
  'Judges': 'Jueces',
  'Judg': 'Jue',
  'Judg.': 'Jue',
  'Ruth': 'Rut',
  '1 Samuel': '1 Samuel',
  '1 Sam': '1 S',
  '1 Sam.': '1 S',
  '2 Samuel': '2 Samuel',
  '2 Sam': '2 S',
  '2 Sam.': '2 S',
  '1 Kings': '1 Reyes',
  '1 Kgs': '1 R',
  '1 Kgs.': '1 R',
  '2 Kings': '2 Reyes',
  '2 Kgs': '2 R',
  '2 Kgs.': '2 R',
  '1 Chronicles': '1 Crónicas',
  '1 Chron': '1 Cr',
  '1 Chron.': '1 Cr',
  '1 Chro': '1 Cr',
  '1 Chro.': '1 Cr',
  '2 Chronicles': '2 Crónicas',
  '2 Chron': '2 Cr',
  '2 Chron.': '2 Cr',
  '2 Chro': '2 Cr',
  '2 Chro.': '2 Cr',
  'Ezra': 'Esdras',
  'Nehemiah': 'Nehemías',
  'Neh': 'Neh',
  'Neh.': 'Neh',
  'Esther': 'Ester',
  'Est': 'Est',
  'Est.': 'Est',

  // Libros poéticos / sapienciales
  'Job': 'Job',
  'Psalms': 'Salmos',
  'Psalm': 'Salmo',
  'Ps': 'Sal',
  'Ps.': 'Sal',
  'Proverbs': 'Proverbios',
  'Prov': 'Pr',
  'Prov.': 'Pr',
  'Pro': 'Pr',
  'Pro.': 'Pr',
  'Ecclesiastes': 'Eclesiastés',
  'Eccl': 'Ec',
  'Eccl.': 'Ec',
  'Song of Solomon': 'Cantares',
  'Song': 'Cnt',
  'S.O.S.': 'Cnt',
  'SOS': 'Cnt',

  // Profetas mayores
  'Isaiah': 'Isaías',
  'Isa': 'Is',
  'Isa.': 'Is',
  'Jeremiah': 'Jeremías',
  'Jer': 'Jer',
  'Jer.': 'Jer',
  'Lamentations': 'Lamentaciones',
  'Lamentation': 'Lamentaciones',
  'Lam': 'Lm',
  'Lam.': 'Lm',
  'Ezekiel': 'Ezequiel',
  'Ezek': 'Ez',
  'Ezek.': 'Ez',
  'Daniel': 'Daniel',
  'Dan': 'Dn',
  'Dan.': 'Dn',

  // Profetas menores
  'Hosea': 'Oseas',
  'Hos': 'Os',
  'Hos.': 'Os',
  'Joel': 'Joel',
  'Amos': 'Amós',
  'Obadiah': 'Abdías',
  'Oba': 'Abd',
  'Oba.': 'Abd',
  'Obad': 'Abd',
  'Obad.': 'Abd',
  'Jonah': 'Jonás',
  'Jon': 'Jon',
  'Jon.': 'Jon',
  'Micah': 'Miqueas',
  'Mic': 'Mi',
  'Mic.': 'Mi',
  'Nahum': 'Nahúm',
  'Nah': 'Nah',
  'Nah.': 'Nah',
  'Habakkuk': 'Habacuc',
  'Hab': 'Hab',
  'Hab.': 'Hab',
  'Zephaniah': 'Sofonías',
  'Zeph': 'Sof',
  'Zeph.': 'Sof',
  'Haggai': 'Hageo',
  'Hag': 'Hag',
  'Hag.': 'Hag',
  'Zechariah': 'Zacarías',
  'Zech': 'Zac',
  'Zech.': 'Zac',
  'Malachi': 'Malaquías',
  'Mal': 'Mal',
  'Mal.': 'Mal',

  // Evangelios
  'Matthew': 'Mateo',
  'Matt': 'Mt',
  'Matt.': 'Mt',
  'Mark': 'Marcos',
  'Mk': 'Mr',
  'Mk.': 'Mr',
  'Luke': 'Lucas',
  'Lk': 'Lc',
  'Lk.': 'Lc',
  'John': 'Juan',
  'Jn': 'Jn',
  'Jn.': 'Jn',

  // Historia
  'Acts': 'Hechos',
  'Act': 'Hch',
  'Act.': 'Hch',

  // Epístolas paulinas
  'Romans': 'Romanos',
  'Rom': 'Ro',
  'Rom.': 'Ro',
  '1 Corinthians': '1 Corintios',
  '1 Cor': '1 Co',
  '1 Cor.': '1 Co',
  '2 Corinthians': '2 Corintios',
  '2 Cor': '2 Co',
  '2 Cor.': '2 Co',
  'Galatians': 'Gálatas',
  'Gal': 'Gá',
  'Gal.': 'Gá',
  'Ephesians': 'Efesios',
  'Eph': 'Ef',
  'Eph.': 'Ef',
  'Philippians': 'Filipenses',
  'Phil': 'Fil',
  'Phil.': 'Fil',
  'Colossians': 'Colosenses',
  'Col': 'Col',
  'Col.': 'Col',
  '1 Thessalonians': '1 Tesalonicenses',
  '1 Thess': '1 Ts',
  '1 Thess.': '1 Ts',
  '2 Thessalonians': '2 Tesalonicenses',
  '2 Thess': '2 Ts',
  '2 Thess.': '2 Ts',
  '1 Timothy': '1 Timoteo',
  '1 Tim': '1 Ti',
  '1 Tim.': '1 Ti',
  '2 Timothy': '2 Timoteo',
  '2 Tim': '2 Ti',
  '2 Tim.': '2 Ti',
  'Titus': 'Tito',
  'Tit': 'Tit',
  'Tit.': 'Tit',
  'Philemon': 'Filemón',
  'Phlm': 'Flm',
  'Phlm.': 'Flm',

  // Epístolas generales
  'Hebrews': 'Hebreos',
  'Hebrew': 'Hebreos',
  'Heb': 'He',
  'Heb.': 'He',
  'James': 'Santiago',
  'Jas': 'Stg',
  'Jas.': 'Stg',
  '1 Peter': '1 Pedro',
  '1 Pet': '1 P',
  '1 Pet.': '1 P',
  '2 Peter': '2 Pedro',
  '2 Pet': '2 P',
  '2 Pet.': '2 P',
  '1 John': '1 Juan',
  '1 Jn': '1 Jn',
  '1 Jn.': '1 Jn',
  '2 John': '2 Juan',
  '2 Jn': '2 Jn',
  '2 Jn.': '2 Jn',
  '3 John': '3 Juan',
  '3 Jn': '3 Jn',
  '3 Jn.': '3 Jn',
  'Jude': 'Judas',

  // Apocalíptico
  'Revelation': 'Apocalipsis',
  'Rev': 'Ap',
  'Rev.': 'Ap',
};

// Haitian Creole — names follow the Haitian Bible Society edition
// (Bib Kreyòl La / Bib la an Kreyòl Ayisyen) which is the most widely-read
// Protestant translation in Haiti.
export const bibleBooksCreole: Record<string, string> = {
  // Ansyen Testaman
  'Genesis': 'Jenèz',
  'Gen': 'Jen',
  'Gen.': 'Jen',
  'Exodus': 'Egzòd',
  'Exod': 'Egz',
  'Exod.': 'Egz',
  'Leviticus': 'Levitik',
  'Lev': 'Lev',
  'Lev.': 'Lev',
  'Numbers': 'Resansman',
  'Num': 'Res',
  'Num.': 'Res',
  'Deuteronomy': 'Detewonòm',
  'Deut': 'Det',
  'Deut.': 'Det',

  // Liv istorik
  'Joshua': 'Jozye',
  'Josh': 'Joz',
  'Josh.': 'Joz',
  'Judges': 'Jij',
  'Judg': 'Jij',
  'Judg.': 'Jij',
  'Ruth': 'Rit',
  '1 Samuel': '1 Samyèl',
  '1 Sam': '1 Sam',
  '1 Sam.': '1 Sam',
  '2 Samuel': '2 Samyèl',
  '2 Sam': '2 Sam',
  '2 Sam.': '2 Sam',
  '1 Kings': '1 Wa',
  '1 Kgs': '1 Wa',
  '1 Kgs.': '1 Wa',
  '2 Kings': '2 Wa',
  '2 Kgs': '2 Wa',
  '2 Kgs.': '2 Wa',
  '1 Chronicles': '1 Istwa',
  '1 Chron': '1 Ist',
  '1 Chron.': '1 Ist',
  '1 Chro': '1 Ist',
  '1 Chro.': '1 Ist',
  '2 Chronicles': '2 Istwa',
  '2 Chron': '2 Ist',
  '2 Chron.': '2 Ist',
  '2 Chro': '2 Ist',
  '2 Chro.': '2 Ist',
  'Ezra': 'Esdras',
  'Nehemiah': 'Neyemi',
  'Neh': 'Ney',
  'Neh.': 'Ney',
  'Esther': 'Estè',
  'Est': 'Estè',
  'Est.': 'Estè',

  // Liv pwetik / sajès
  'Job': 'Jòb',
  'Psalms': 'Sòm',
  'Psalm': 'Sòm',
  'Ps': 'Sòm',
  'Ps.': 'Sòm',
  'Proverbs': 'Pwovèb',
  'Prov': 'Pwo',
  'Prov.': 'Pwo',
  'Pro': 'Pwo',
  'Pro.': 'Pwo',
  'Ecclesiastes': 'Eklezyas',
  'Eccl': 'Ekl',
  'Eccl.': 'Ekl',
  'Song of Solomon': 'Chante Salomon',
  'Song': 'Ch',
  'S.O.S.': 'Ch',
  'SOS': 'Ch',

  // Gwo pwofèt
  'Isaiah': 'Ezayi',
  'Isa': 'Eza',
  'Isa.': 'Eza',
  'Jeremiah': 'Jeremi',
  'Jer': 'Jer',
  'Jer.': 'Jer',
  'Lamentations': 'Plenn',
  'Lamentation': 'Plenn',
  'Lam': 'Pl',
  'Lam.': 'Pl',
  'Ezekiel': 'Ezekyèl',
  'Ezek': 'Eze',
  'Ezek.': 'Eze',
  'Daniel': 'Danyèl',
  'Dan': 'Dan',
  'Dan.': 'Dan',

  // Ti pwofèt
  'Hosea': 'Oze',
  'Hos': 'Oze',
  'Hos.': 'Oze',
  'Joel': 'Jowèl',
  'Amos': 'Amòs',
  'Obadiah': 'Abdyas',
  'Oba': 'Abd',
  'Oba.': 'Abd',
  'Obad': 'Abd',
  'Obad.': 'Abd',
  'Jonah': 'Jonas',
  'Jon': 'Jon',
  'Jon.': 'Jon',
  'Micah': 'Miche',
  'Mic': 'Mi',
  'Mic.': 'Mi',
  'Nahum': 'Nawoum',
  'Nah': 'Naw',
  'Nah.': 'Naw',
  'Habakkuk': 'Abakouk',
  'Hab': 'Aba',
  'Hab.': 'Aba',
  'Zephaniah': 'Sofoni',
  'Zeph': 'Sof',
  'Zeph.': 'Sof',
  'Haggai': 'Aje',
  'Hag': 'Aje',
  'Hag.': 'Aje',
  'Zechariah': 'Zakari',
  'Zech': 'Zak',
  'Zech.': 'Zak',
  'Malachi': 'Malachi',
  'Mal': 'Mal',
  'Mal.': 'Mal',

  // Nouvo Testaman / Levanjil
  'Matthew': 'Matye',
  'Matt': 'Mat',
  'Matt.': 'Mat',
  'Mark': 'Mak',
  'Mk': 'Mak',
  'Mk.': 'Mak',
  'Luke': 'Lik',
  'Lk': 'Lik',
  'Lk.': 'Lik',
  'John': 'Jan',
  'Jn': 'Jan',
  'Jn.': 'Jan',

  // Istwa
  'Acts': 'Travay',
  'Act': 'Trav',
  'Act.': 'Trav',

  // Lèt Pòl yo
  'Romans': 'Women',
  'Rom': 'Wo',
  'Rom.': 'Wo',
  '1 Corinthians': '1 Korent',
  '1 Cor': '1 Ko',
  '1 Cor.': '1 Ko',
  '2 Corinthians': '2 Korent',
  '2 Cor': '2 Ko',
  '2 Cor.': '2 Ko',
  'Galatians': 'Galat',
  'Gal': 'Gal',
  'Gal.': 'Gal',
  'Ephesians': 'Efezyen',
  'Eph': 'Ef',
  'Eph.': 'Ef',
  'Philippians': 'Filipyen',
  'Phil': 'Fil',
  'Phil.': 'Fil',
  'Colossians': 'Kolosyen',
  'Col': 'Kol',
  'Col.': 'Kol',
  '1 Thessalonians': '1 Tesalonisyen',
  '1 Thess': '1 Tes',
  '1 Thess.': '1 Tes',
  '2 Thessalonians': '2 Tesalonisyen',
  '2 Thess': '2 Tes',
  '2 Thess.': '2 Tes',
  '1 Timothy': '1 Timote',
  '1 Tim': '1 Tim',
  '1 Tim.': '1 Tim',
  '2 Timothy': '2 Timote',
  '2 Tim': '2 Tim',
  '2 Tim.': '2 Tim',
  'Titus': 'Tit',
  'Tit': 'Tit',
  'Tit.': 'Tit',
  'Philemon': 'Filemon',
  'Phlm': 'Flm',
  'Phlm.': 'Flm',

  // Lèt jeneral
  'Hebrews': 'Ebre',
  'Hebrew': 'Ebre',
  'Heb': 'Ebr',
  'Heb.': 'Ebr',
  'James': 'Jak',
  'Jas': 'Jak',
  'Jas.': 'Jak',
  '1 Peter': '1 Pyè',
  '1 Pet': '1 Pyè',
  '1 Pet.': '1 Pyè',
  '2 Peter': '2 Pyè',
  '2 Pet': '2 Pyè',
  '2 Pet.': '2 Pyè',
  '1 John': '1 Jan',
  '1 Jn': '1 Jan',
  '1 Jn.': '1 Jan',
  '2 John': '2 Jan',
  '2 Jn': '2 Jan',
  '2 Jn.': '2 Jan',
  '3 John': '3 Jan',
  '3 Jn': '3 Jan',
  '3 Jn.': '3 Jan',
  'Jude': 'Jid',

  // Apokalips
  'Revelation': 'Revelasyon',
  'Rev': 'Rev',
  'Rev.': 'Rev',
};

// Per-locale lookup. English has no map (book name == lookup key).
const LOCALE_BOOK_MAPS: Partial<Record<Locale, Record<string, string>>> = {
  fr: bibleBooksFr,
  es: bibleBooksEs,
  creole: bibleBooksCreole,
};

function getMapForLocale(locale: Locale): Record<string, string> | undefined {
  return LOCALE_BOOK_MAPS[locale];
}

/**
 * Translates a Bible passage reference from English into the target locale.
 * Handles formats like "Genesis 1", "Gen. 1-3", "1 Cor. 5".
 * Falls back to the original passage when no map exists for the locale
 * or when the book name is unknown.
 */
export function translatePassage(passage: string, locale: Locale): string {
  const map = getMapForLocale(locale);
  // English (or any locale without a map) returns the passage as-is.
  if (!map) {
    return passage;
  }

  // Numbered books (1 Samuel, 2 Kings, etc.)
  const numberedBookMatch = passage.match(/^(\d\s+\w+\.?)\s*(.*)$/);
  if (numberedBookMatch) {
    const [, bookName, reference] = numberedBookMatch;
    const translated = map[bookName.trim()];
    if (translated) {
      return reference ? `${translated} ${reference}` : translated;
    }
  }

  // Dotted abbreviations (S.O.S., Gen., etc.)
  const dottedMatch = passage.match(/^([A-Za-z]+(?:\.[A-Za-z]*)*\.?)\s*(.*)$/);
  if (dottedMatch) {
    const [, bookName, reference] = dottedMatch;
    const translated = map[bookName.trim()];
    if (translated) {
      return reference ? `${translated} ${reference}` : translated;
    }
  }

  // Standard single-word book name
  const standardMatch = passage.match(/^([A-Za-z]+\.?)\s*(.*)$/);
  if (standardMatch) {
    const [, bookName, reference] = standardMatch;
    const translated = map[bookName.trim()];
    if (translated) {
      return reference ? `${translated} ${reference}` : translated;
    }
  }

  // No translation found — return the original so the user still sees the reference.
  return passage;
}

/**
 * Translate an array of Bible passages.
 */
export function translatePassages(passages: string[], locale: Locale): string[] {
  return passages.map((passage) => translatePassage(passage, locale));
}

/**
 * Translate a single book name (full or abbreviated) into the target locale.
 * Falls back to the original name if no translation is available.
 */
export function getBookName(bookName: string, locale: Locale): string {
  const map = getMapForLocale(locale);
  if (!map) {
    return bookName;
  }
  return map[bookName] ?? bookName;
}
